import { BlockEditProps } from "@wordpress/blocks";
import React from "react";
import type { BlockAttributes } from "./block.json";
import {
  InspectorControls,
  RichText,
  useBlockProps,
  useInnerBlocksProps,
} from "@wordpress/block-editor";
import ImageEditor from "../../../source/components/ImageEditor";
import {
  ToggleControl,
  PanelBody,
  PanelRow,
  TextControl,
} from "@wordpress/components";

const ALLOWED_BLOCKS = ["core/paragraph", "core/heading", "yellowtree/button"];

export default function ColumnEditor(props: BlockEditProps<BlockAttributes>) {
  const blockProps = useBlockProps({
    className: "alignfull position-relative z-1",
  });
  const innerBlocksProps = useInnerBlocksProps({
    allowedBlocks: ALLOWED_BLOCKS,
    template: [
      [
        "core/paragraph",
        {
          placeholder: "First paragraph placeholder text",
        },
      ],
      [
        "core/paragraph",
        {
          placeholder: "Second paragraph placeholder text",
        },
      ],
      [
        "core/paragraph",
        {
          placeholder: "Third paragraph placeholder text",
        },
      ],
    ],
  });
  return (
    <>
      <InspectorControls>
        <PanelBody title="Layout Settings" initialOpen={true}>
          <PanelRow>
            <ToggleControl
              label="Bild rechts"
              checked={props.attributes["image-right"]}
              onChange={(value) =>
                props.setAttributes({ "image-right": value })
              }
            />
          </PanelRow>
        </PanelBody>
        <PanelBody title="Style" initialOpen={true}>
          <PanelRow>
            <ToggleControl
              label="Dekor"
              checked={props.attributes["decoration"]}
              onChange={(value) => props.setAttributes({ decoration: value })}
            />
          </PanelRow>
        </PanelBody>
      </InspectorControls>

      <div {...blockProps}>
        <div className="row me-0 ms-0 full-2-column">
          <div
            className={`col-xl-6 p-0 position-relative d-flex align-items-center justify-content-center ${
              props.attributes["image-right"]
                ? "order-1 order-xl-2"
                : "order-xl-1"
            }`}
          >
            <ImageEditor
              attributes={props.attributes}
              setAttributes={props.setAttributes}
              prefix="image"
              placeholderClassName="img-placeholder"
            />
          </div>
          <div
            className={`col-xl-6 d-flex align-items-center justify-content-center ${
              props.attributes["image-right"]
                ? "order-xl-1 justify-content-center order-2"
                : "order-xl-2"
            }`}
          >
            <div className="content position-relative z-1">
              {props.attributes.decoration && (
                <svg
                  className="decoration-top z-0"
                  width="308"
                  height="380"
                  viewBox="50 50 300 200"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M214.3,266.2q-.08.45-.16.89.47.51,1.02.58l.71-.83c.24-1.34-.23-1.85-.78-1.92l-.79,1.28ZM65.04,190.69c1.66.19,4.92-11.74,10.42-36.2,2.71-12,5.35-23.56,7.43-35.18.72-4.02,1.28-7.15,1.13-9.45.4-2.24,6.8-6.5,19.82-13.19,13.03-6.68,20.36-9.93,22.02-9.73.47.51,1.49,1.09,2.6,1.22l1.74-.25q.55.07.47.51l-.16.89-.32,1.79c-.24,1.34.53,6.45,2.32,15.33,3.35,15.91,8.25,38.85,15.9,68.5,5.7,21.66,9.37,32.59,9.92,32.66.55.06.63-.38.79-1.28l.16-.89c-.3-4.6.11-13.22.62-25.49,1.06-12.2,2.2-24.84,2.7-37.11,1.14-12.65,2.19-21.65,2.52-26.63l21.55-10.25c13.66-7.07,21-10.31,22.1-10.18,1.1.13,5.46,23.01,13.06,68.63,7.29,44.22,12.8,76.35,15.51,95.84l1.63,9.78c.86,4.66,1.32,8.37.92,10.6-.4,2.24-3.17,5.1-8.69,7.65-19.42,10.95-30.55,16.49-33.31,16.16-1.1-.13-2.12-3.9-2.49-11.25-.69-8.75-1.76-18.46-3.31-28.69-3.95-25.11-6.21-37.7-6.76-37.76-1.76,9.83-4.73,29.57-8.91,59.2-3.16,1.91-6.87,3.76-10.66,6.05-3.16,1.91-6.79,3.31-9.79,4.33-1.1-.13-1.73.25-1.89,1.15-.08.45-3.24,2.36-9.4,5.29-5.6,2.99-9.23,4.39-10.89,4.2-3.86-.45-9.79-23.97-17.31-70.04-2.32-15.33-5.66-34.44-10.17-56.42-1.11,3.06-1.59,5.75-2.7,8.81-1.11,3.07-3.34,12.39-6.7,27.97l-31.86-26.56Z"
                    style={{ fill: "#6d6af0" }}
                  ></path>
                  <path
                    d="M318.35,98.53l8.88.61-4.18-.75c-2.64-.59-4.17-.75-4.61-.48q-.43.27-.1.62M332.18,99.97c2.3.24,4.27.13,4.71-.14l.44-.27-4.51-1.1-5.38-.56c.67.7,2.11,1.48,4.74,2.08M244.14,22.78l17.64,58.46c7.76,24.85,11.59,37.58,12.26,38.29.1-.62.3-1.87.93-3.38.3-1.87,1.36-3.65,1.66-5.52.73-2.14,1.13-4.63,2.29-7.04,1.16-2.41,1.9-4.55,2.53-6.06.3-1.87,1.7-3.3,3.43-4.39,4.77-2.98,14.38-4.51,29.17-4.23,8.64-.36,14.22-1.05,15.52-1.86.87-.54.97-1.16.63-1.52-.67-.7-3.11-2.54-7.32-5.51-4.54-3.32-10.19-7.07-16.94-11.26-7.08-4.54-13.84-8.72-19.48-12.47-5.98-4.1-10.63-6.8-13.84-8.72-3.21-1.92-6.42-3.83-9.96-6.1l10.94-39.34c.43-.27,1.63-.46,2.74-.03,20.02,11.58,37.5,21.95,51.57,31.64,14.07,9.69,33.26,24.03,58.23,43.72l-.08,7.9,9.76,7.35c-2.45,10.5-3.77,16.37-4.3,17.26-.1.62-.73,2.14-1.03,4-2.45,10.5-4.54,16.29-5.84,17.1-.43.27-.87.54-1.3.81-.43.27-1.2.19-3.51-.05-1.87-.51-4.84-1.45-8.25-2.13-3.31-1.29-7.05-2.32-10.79-3.34-3.31-1.29-7.48-2.05-11.99-3.15-4.51-1.1-7.91-1.77-10.89-2.72-10.65-1.74-21.07-2.52-31.78-1.42-11.05.74-18.79,2.78-23.56,5.76-2.17,1.36-3.76,4.04-4.26,7.15-.49,3.11.59,8.6,1.7,16.31,1.08,5.49,1.92,10.01,1.76,13.47.65,5.76-.98,13.5-3.76,23.65l-.4,2.49c-2.55,11.12-4.64,16.91-5.94,17.72-.43.27-.77-.08-1.77-1.13-1-1.05-1.34-1.4-1.24-2.03-2.34-2.46-3.28-6.35-2.83-11.68.93-3.38.15-10.74-2.25-22.69-2.73-12.3-8.11-32.47-16.14-60.51-3.56-9.54-7.96-23.6-13.2-42.18-4.54-15.66-9.51-31.04-14.24-45.45l24.14,2.84Z"
                    style={{ fill: "#6d6af0" }}
                  ></path>
                </svg>
              )}
              <div {...innerBlocksProps}></div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
